// src/pages/AdminMovieImport.jsx (enhanced FULL)
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_API_BASE ?? "";

async function getJSON(path, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: { Accept: "application/json", ...(opts.headers || {}) },
    ...opts,
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// BG жанрове по TMDb ID
const genreMap = {
  28: "Екшън", 12: "Приключение", 16: "Анимация", 35: "Комедия", 80: "Криминален",
  99: "Документален", 18: "Драма", 10751: "Семеен", 14: "Фентъзи", 36: "Исторически",
  27: "Ужаси", 10402: "Музикален", 9648: "Мистерия", 10749: "Романтичен",
  878: "Фантастика", 10770: "Телевизионен", 53: "Трилър", 10752: "Военен", 37: "Уестърн",
};

// Възрастови категории (BG)
const BG_CATEGORIES = ["A", "B", "C", "C+", "D", "D+", "X", "N/A"];

// Полета, които считаме задължителни / полезни
const REQUIRED_FIELDS = [
  "MOVIE_NAME",
  "PLOT",
  "GENRE",
  "POSTER_URL",
  "TRAILER_URL",
  "DIRECTOR",
  "ACTORS",
  "DURATION",
  "PREMIERE_DATE",
  "ORIGINAL_LANGUAGE",
  "CATEGORY",
];

// helpers
const toYmd = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "";
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${dt.getFullYear()}-${mm}-${dd}`;
};
const sanitize = (v) => (v === undefined ? null : v);
const isEmpty = (v) => v === undefined || v === null || String(v).trim() === "";

export default function AdminMovieImport({ hideBack = false }) {
  const navigate = useNavigate();

  // UI
  const [tab, setTab] = useState("tmdb"); // "tmdb" | "existing"
  const [busy, setBusy] = useState(false);

  // Данни
  const [movies, setMovies] = useState([]);
  const [search, setSearch] = useState("");
  const [tmdbResults, setTmdbResults] = useState([]);

  // Филтри за съществуващи
  const [filterText, setFilterText] = useState("");
  const [filterGenre, setFilterGenre] = useState("");
  const [onlyIncomplete, setOnlyIncomplete] = useState(false);

  // Редакция
  const [editingMovie, setEditingMovie] = useState(null);
  const [editTab, setEditTab] = useState("main"); // main | media | classify | people | tmdb

  // TMDb помощник – локално състояние в рамките на модала
  const [tmdbQuery, setTmdbQuery] = useState("");
  const [tmdbLang, setTmdbLang] = useState("bg-BG");
  const [tmdbModalResults, setTmdbModalResults] = useState([]);
  const [tmdbPicked, setTmdbPicked] = useState(null); // {id, title, ...}

  const loadMovies = async () => {
    setBusy(true);
    try {
      const data = await getJSON("/api/movies");
      setMovies(Array.isArray(data) ? data : []);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    loadMovies().catch(console.error);
  }, []);

  const handleSearchTMDB = async () => {
    if (!search.trim()) return;
    try {
      setBusy(true);
      const data = await getJSON(
        `/api/tmdb?query=${encodeURIComponent(search)}&lang=${encodeURIComponent("bg-BG")}`
      );
      setTmdbResults(data?.results || []);
      setTab("tmdb");
    } catch (err) {
      console.error("TMDB fetch failed:", err);
      alert("❌ TMDb заявката се провали. Виж конзолата.");
    } finally {
      setBusy(false);
    }
  };

  // По-подробни данни от TMDb (поддържа lang, за предпочитание bg)
  const fetchDetails = async (id, lang = "bg-BG") => {
    try {
      const details = await getJSON(`/api/tmdb/details/${id}?lang=${encodeURIComponent(lang)}`);
      return normalizeDetails(details);
    } catch (e) {
      console.warn("/api/tmdb/details fallback", e);
      return {
        original_title: "",
        overview: "",
        release_date: "",
        runtime: 120,
        genres: "",
        production: "",
        language: "",
        poster: "",
        trailer: "",
        director: "",
        actors: "",
        certifications: [],
        imdb_id: "",
      };
    }
  };

  // Нормализиране на детайли от бекенда към очакваните ключове
  function normalizeDetails(d) {
    const genresStr = Array.isArray(d?.genres)
      ? d.genres.map((g) => (typeof g === "string" ? g : g?.name)).filter(Boolean).join(", ")
      : d?.genres || "";

    return {
      original_title: d?.originalTitle || d?.original_title || "",
      overview: d?.plot_bg || d?.overview_bg || d?.plot || d?.overview || "", // предпочитаме BG, ако има
      release_date: d?.premiere || d?.release_date || "",
      runtime: d?.duration || d?.runtime || 120,
      genres: genresStr,
      production: d?.production || "",
      language: d?.originalLanguage || d?.original_language || "",
      poster: d?.poster || d?.poster_url || "",
      trailer: d?.trailerUrl || d?.trailer || "",
      director: d?.director || "",
      actors: d?.actors || "",
      certifications: d?.certifications || [], // TMDb-like
      imdb_id: d?.imdb_id || d?.external_ids?.imdb_id || "",
    };
  }

  // Сертификати (възраст). Очакван отговор – TMDb release_dates style.
  const fetchCertifications = async (id) => {
    try {
      const data = await getJSON(`/api/tmdb/certifications/${id}`);
      return data; // оставяме суров, ще го четем в mapCertificationToBG
    } catch (e) {
      console.warn("No certifications endpoint, using details() as fallback");
      const d = await fetchDetails(id, tmdbLang);
      return { results: [{ iso_3166_1: "BG", release_dates: [{ certification: d?.certification || "" }] }] };
    }
  };

  // Мапване към нашите BG категории
  const mapCertificationToBG = (certData) => {
    const pick = (iso) => certData?.results?.find((r) => r.iso_3166_1 === iso);
    const preferred = pick("BG") || pick("DE") || pick("US") || pick("GB");
    const cert = preferred?.release_dates?.find((x) => x.certification)?.certification || "";

    const c = String(cert).toUpperCase();
    if (/(X|NC-17|18\+?|FSK18|R18)/.test(c)) return { label: "18+", category: "D" };
    if (/(16\+?|FSK16)/.test(c)) return { label: "16+", category: "C+" };
    if (/(14\+?|PG-13|12\+?)/.test(c)) return { label: "12+", category: "C" };
    if (/(PG|7\+?)/.test(c)) return { label: "7+", category: "B" };
    if (/(G|ALL|A|U)/.test(c) || c.trim() === "") return { label: "Всички", category: "A" };
    return { label: c, category: "N/A" };
  };

  const importMovie = async (movie) => {
    try {
      setBusy(true);
      const extra = await fetchDetails(movie.id, "bg-BG");
      const translatedGenres = (movie.genre_ids || [])
        .map((id) => genreMap[id])
        .filter(Boolean)
        .join(", ");

      const payload = {
        MOVIE_NAME: movie.title || "",
        ORIGINAL_TITLE: extra.original_title || "",
        DURATION: Number(extra.runtime) || 0,
        IMDB_RATING: null,
        TRAILER_URL: extra.trailer || "",
        POSTER_URL: extra.poster || "",
        PLOT: extra.overview || "",
        GENRE: translatedGenres || extra.genres || "",
        ACTORS: extra.actors || "",
        DIRECTOR: extra.director || "",
        ORIGINAL_LANGUAGE: extra.language || "",
        CATEGORY: "",
        BROADCAST_FORMAT: "",
        PREMIERE_DATE: extra.release_date || null,
        PRODUCTION: extra.production || "",
        PHOTO_URL: "",
      };

      const res = await fetch(`${API}/api/movies`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        alert("✅ Успешен импорт");
        setSearch("");
        setTmdbResults([]);
        await loadMovies();
        setTab("existing");
      } else {
        const t = await res.text();
        console.error("POST /api/movies failed:", t);
        alert("❌ Неуспешен импорт.");
      }
    } catch (err) {
      console.error("❌ Import error:", err);
      alert("❌ Грешка при импорта (виж конзолата).");
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteMovie = async (id) => {
    if (!window.confirm("❗ Сигурни ли сте, че искате да изтриете филма?")) return;
    const res = await fetch(`${API}/api/movies/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (res.ok) loadMovies();
  };

  const saveEdit = async () => {
    const m = editingMovie || {};
    const payload = {
      MOVIE_NAME: sanitize(m.MOVIE_NAME ?? ""),
      ORIGINAL_TITLE: sanitize(m.ORIGINAL_TITLE ?? ""),
      DURATION: Number(m.DURATION) || 0,
      IMDB_RATING: m.IMDB_RATING !== undefined && m.IMDB_RATING !== "" ? Number(m.IMDB_RATING) : null,
      TRAILER_URL: sanitize(m.TRAILER_URL ?? ""),
      POSTER_URL: sanitize(m.POSTER_URL ?? ""),
      PLOT: sanitize(m.PLOT ?? m.DESCRIPTION ?? ""),
      GENRE: sanitize(m.GENRE ?? ""),
      ACTORS: sanitize(m.ACTORS ?? ""),
      DIRECTOR: sanitize(m.DIRECTOR ?? ""),
      ORIGINAL_LANGUAGE: sanitize(m.ORIGINAL_LANGUAGE ?? ""),
      CATEGORY: sanitize(m.CATEGORY ?? ""),
      BROADCAST_FORMAT: sanitize(m.BROADCAST_FORMAT ?? ""),
      PREMIERE_DATE: sanitize(m.PREMIERE_DATE ? toYmd(m.PREMIERE_DATE) : null),
      PRODUCTION: sanitize(m.PRODUCTION ?? ""),
      PHOTO_URL: sanitize(m.PHOTO_URL ?? ""),
    };

    const res = await fetch(`${API}/api/movies/${editingMovie.MOVIE_ID}`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (res.ok) {
      alert("✅ Записано");
      setEditingMovie(null);
      loadMovies();
    } else {
      const t = await res.text();
      console.error("PUT /api/movies failed:", t);
      alert("❌ Неуспешно обновяване");
    }
  };

  const formatDate = (date) => (!date ? "" : new Date(date).toLocaleDateString("bg-BG"));

  // Откриване на празни полета
  const getMissingFields = (m) => {
    const list = [];
    for (const f of REQUIRED_FIELDS) {
      if (isEmpty(m[f]) && !(f === "TRAILER_URL" && isEmpty(m["POSTER_URL"]))) {
        list.push(f);
      }
    }
    return list;
  };

  // Филтрирани съществуващи
  const filteredMovies = useMemo(() => {
    const f = (movies || []).filter((m) => {
      const t = (filterText || "").toLowerCase();
      const g = (filterGenre || "").toLowerCase();
      const name = (m.MOVIE_NAME || "").toLowerCase();
      const genres = (m.GENRE || "").toLowerCase();
      const okText = !t || name.includes(t) || genres.includes(t);
      const okGenre = !g || genres.includes(g);
      const okIncomplete = !onlyIncomplete || getMissingFields(m).length > 0;
      return okText && okGenre && okIncomplete;
    });
    return f.sort((a, b) => {
      const da = new Date(a.PREMIERE_DATE || "1900-01-01").getTime();
      const db = new Date(b.PREMIERE_DATE || "1900-01-01").getTime();
      if (db !== da) return db - da;
      return String(a.MOVIE_NAME).localeCompare(String(b.MOVIE_NAME), "bg");
    });
  }, [movies, filterText, filterGenre, onlyIncomplete]);

  // ---------- TMDb помощник (в модала) ----------
  const tmdbSearchInModal = async () => {
    if (!tmdbQuery.trim()) return;
    try {
      setBusy(true);
      const data = await getJSON(
        `/api/tmdb?query=${encodeURIComponent(tmdbQuery)}&lang=${encodeURIComponent(tmdbLang)}`
      );
      setTmdbModalResults(data?.results || []);
    } catch (e) {
      console.error(e);
      alert("❌ TMDb търсене неуспешно");
    } finally {
      setBusy(false);
    }
  };

  const applyDetailsToEditing = (details, mode = "fill-empty") => {
    setEditingMovie((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      const patch = {
        ORIGINAL_TITLE: details.original_title || next.ORIGINAL_TITLE,
        PLOT: details.overview || next.PLOT,
        PREMIERE_DATE: details.release_date || next.PREMIERE_DATE,
        DURATION: details.runtime || next.DURATION,
        GENRE: details.genres || next.GENRE,
        PRODUCTION: details.production || next.PRODUCTION,
        ORIGINAL_LANGUAGE: details.language || next.ORIGINAL_LANGUAGE,
        POSTER_URL: details.poster || next.POSTER_URL,
        TRAILER_URL: details.trailer || next.TRAILER_URL,
        DIRECTOR: details.director || next.DIRECTOR,
        ACTORS: details.actors || next.ACTORS,
      };

      for (const k of Object.keys(patch)) {
        if (mode === "overwrite-all") {
          next[k] = patch[k];
        } else {
          if (isEmpty(next[k]) && !isEmpty(patch[k])) next[k] = patch[k];
        }
      }
      return next;
    });
  };

  const setCategoryFromTMDb = async () => {
    if (!tmdbPicked?.id) {
      alert("Моля изберете заглавие от TMDb резултатите.");
      return;
    }
    try {
      setBusy(true);
      const data = await fetchCertifications(tmdbPicked.id);
      const { label, category } = mapCertificationToBG(data);
      setEditingMovie((p) => ({ ...p, CATEGORY: category }));
      alert(`✅ Възрастова категория: ${label} → BG: ${category}`);
    } catch (e) {
      console.error(e);
      alert("❌ Неуспешно определяне на възрастовата категория");
    } finally {
      setBusy(false);
    }
  };

  // ---- Универсални помощници за външни източници ----
  const fetchFromSources = async (tmdbId, fallbackTitle, fallbackYear, lang = "bg-BG") => {
    const bag = {};

    // 1) TMDb
    let d = null;
    if (tmdbId) {
      d = await fetchDetails(tmdbId, lang);
      if (d) Object.assign(bag, d);
    }

    // 2) OMDb (през бекенд прокси /api/omdb)
    try {
      const imdb = d?.imdb_id;
      if (imdb) {
        const om = await getJSON(`/api/omdb?i=${encodeURIComponent(imdb)}&lang=${encodeURIComponent(lang)}`);
        if (om) {
          if (!bag.language && (om.Language || om.language)) bag.language = (om.Language || om.language).split(",")[0];
          if (!bag.original_title && om.Title) bag.original_title = om.Title;
          if (!bag.runtime && om.Runtime) bag.runtime = parseInt(String(om.Runtime).replace(/\D/g, ""), 10);
          if (!bag.overview && om.Plot) bag.overview = om.Plot;
        }
      } else if (fallbackTitle) {
        const om = await getJSON(
          `/api/omdb?t=${encodeURIComponent(fallbackTitle)}${fallbackYear ? `&y=${fallbackYear}` : ""}&lang=${encodeURIComponent(lang)}`
        );
        if (om) {
          if (!bag.language && (om.Language || om.language)) bag.language = (om.Language || om.language).split(",")[0];
          if (!bag.original_title && om.Title) bag.original_title = om.Title;
          if (!bag.runtime && om.Runtime) bag.runtime = parseInt(String(om.Runtime).replace(/\D/g, ""), 10);
          if (!bag.overview && om.Plot) bag.overview = om.Plot;
        }
      }
    } catch (e) {
      console.warn("OMDb fallback skipped", e);
    }

    // 3) Wikipedia/Wikidata (през /api/wiki/summary)
    try {
      const titleForWiki = d?.original_title || fallbackTitle;
      if (titleForWiki) {
        const wk = await getJSON(
          `/api/wiki/summary?title=${encodeURIComponent(titleForWiki)}&lang=${encodeURIComponent(lang)}`
        );
        if (wk) {
          if (!bag.overview && (wk.extract || wk.summary)) bag.overview = wk.extract || wk.summary;
          if (!bag.language && wk.lang) bag.language = wk.lang;
        }
      }
    } catch (e) {
      console.warn("Wiki fallback skipped", e);
    }

    return bag;
  };

  const tryResolveTmdbId = async (title, lang = "bg-BG") => {
    if (!title) return null;
    try {
      const data = await getJSON(`/api/tmdb?query=${encodeURIComponent(title)}&lang=${encodeURIComponent(lang)}`);
      return data?.results?.[0]?.id ?? null;
    } catch (e) {
      console.warn("TMDb resolve failed", e);
      return null;
    }
  };

  const smartFillLanguageInline = async () => {
    try {
      setBusy(true);
      const title = editingMovie?.MOVIE_NAME || "";
      const year = String(editingMovie?.PREMIERE_DATE || "").slice(0, 4);
      const id = tmdbPicked?.id || (await tryResolveTmdbId(title, tmdbLang));
      const bag = await fetchFromSources(id, title, year, tmdbLang);
      const langCode = bag.language || "";
      if (!langCode) {
        alert("Не намерих оригинален език от източниците.");
      } else {
        setEditingMovie((p) => ({ ...p, ORIGINAL_LANGUAGE: langCode }));
        alert("✅ Открит оригинален език: " + langCode);
      }
    } catch (e) {
      console.error(e);
      alert("❌ Грешка при определяне на език");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen relative p-4 md:p-6">
      {/* Фон */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <img
          src="https://latonacinema.com/wp-content/uploads/2025/06/cinema-hall-with-rows-of-grey-seats-in-darkness.jpg"
          alt="фон"
          className="h-full w-full object-cover blur-[2px] scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#2c204f]/80 via-black/60 to-[#6c56b5]/70" />
      </div>

      {/* Хедър */}
      <div className="max-w-7xl mx-auto flex items-center gap-3 mb-4">
        {!hideBack && (
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-3 py-2 ... "
            title="Назад"
          >
            ← Назад
          </button>
        )}
        <div className="flex items-center gap-3">

          <img
            src="https://latonacinema.com/wp-content/uploads/2025/06/cropped-latonacinema.png"
            alt="Лого"
            className="h-10"
          />
          <h1 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">🎬 Панел: Филми</h1>
        </div>
        {busy && <span className="ml-auto text-white/70 text-sm">Зареждане…</span>}
      </div>

      {/* Карта с табове */}
      <div className="max-w-7xl mx-auto bg-white/8 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 p-3 border-b border-white/10">
          <TabButton active={tab === "tmdb"} onClick={() => setTab("tmdb")}>
            🔍 Търсене в TMDb
            {tmdbResults.length > 0 && <Badge>{tmdbResults.length}</Badge>}
          </TabButton>
          <TabButton active={tab === "existing"} onClick={() => setTab("existing")}>
            🎞 Съществуващи филми
            {movies.length > 0 && <Badge>{movies.length}</Badge>}
          </TabButton>
        </div>

        {/* Съдържание */}
        {tab === "tmdb" && (
          <div className="p-4 md:p-6">
            <div className="flex flex-col md:flex-row gap-2">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => (e.key === "Enter" ? handleSearchTMDB() : null)}
                placeholder="Въведете заглавие за търсене в TMDb…"
                className="flex-1 px-4 py-2 rounded-xl bg-white/90 text-gray-900 placeholder:text-gray-600
                focus:outline-none focus:ring-2 focus:ring-purple-400 border border-white/40"
                 />
              <button
                onClick={handleSearchTMDB}
                className="px-4 py-2 rounded-xl bg-gradient-to-br from-[#6c56b5] to-[#2c204f] text-white border border-white/20 hover:opacity-95"
              >
                Търси
              </button>
            </div>

            {tmdbResults.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-6">
                {tmdbResults.map((m) => (
                  <div
                    key={m.id}
                    className="group overflow-hidden rounded-2xl bg-white/5 border border-white/10 shadow-md"
                  >
                    <img
                      src={
                        m.poster_path
                          ? `https://image.tmdb.org/t/p/w500${m.poster_path}`
                          : "https://via.placeholder.com/300x450?text=No+Image"
                      }
                      alt={m.title}
                      className="w-full h-64 object-cover group-hover:scale-[1.02] transition"
                    />
                    <div className="p-3 text-white">
                      <div className="font-semibold leading-snug line-clamp-2 min-h-[2.5rem]">{m.title}</div>
                      <div className="text-xs text-white/70 mt-1">
                        {String(m.release_date || "").slice(0, 4) || "—"}
                      </div>
                      <button
                        onClick={() => importMovie(m)}
                        className="mt-3 w-full px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm"
                      >
                        ➕ Импорт
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tmdbResults.length === 0 && (
              <p className="text-white/70 text-sm mt-4">
                Съвет: напишете част от заглавието (на български или английски) и натиснете Enter.
              </p>
            )}
          </div>
        )}

        {tab === "existing" && (
          <div className="p-4 md:p-6">
            {/* Филтри */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <input
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                placeholder="Филтър по заглавие / жанр…"
                className="px-4 py-2 rounded-xl bg-white/90 text-gray-900 placeholder:text-gray-600
                focus:outline-none focus:ring-2 focus:ring-purple-400 border border-white/40"
                 />
              <input
                value={filterGenre}
                onChange={(e) => setFilterGenre(e.target.value)}
                placeholder="Само жанр (например: Драма)…"
                className="px-4 py-2 rounded-xl bg-white/90 text-gray-900 placeholder:text-gray-600
                focus:outline-none focus:ring-2 focus:ring-purple-400 border border-white/40"
                 />
              <label className="flex items-center gap-2 text-white/90 select-none">
                <input
                  type="checkbox"
                  checked={onlyIncomplete}
                  onChange={(e) => setOnlyIncomplete(e.target.checked)}
                />
                Показвай само с празни полета
              </label>
              <button
                onClick={() => {
                  setFilterText("");
                  setFilterGenre("");
                  setOnlyIncomplete(false);
                }}
                className="px-4 py-2 rounded-xl bg-white/10 text-white border border-white/20 hover:bg-white/15"
              >
                Изчисти филтрите
              </button>
            </div>

            {/* Лента с бързи проверки */}
            <div className="mt-4 flex flex-wrap gap-2">
              <QuickAuditButton
                label="Намери без плакат"
                onClick={() => {
                  setOnlyIncomplete(false);
                  setFilterText("");
                  setFilterGenre("");
                  const withoutPoster = movies.filter((m) => isEmpty(m.POSTER_URL));
                  if (withoutPoster.length === 0) return alert("Всички имат плакат ✅");
                  setMovies((prev) => {
                    const miss = new Set(withoutPoster.map((x) => x.MOVIE_ID));
                    return [...prev].sort(
                      (a, b) => Number(miss.has(b.MOVIE_ID)) - Number(miss.has(a.MOVIE_ID))
                    );
                  });
                  setOnlyIncomplete(true);
                }}
              />
              <QuickAuditButton
                label="Намери без сюжет (BG)"
                onClick={() => {
                  setOnlyIncomplete(true);
                  setFilterText("");
                  setFilterGenre("");
                }}
              />
              <QuickAuditButton
                label="Намери без категория (възраст)"
                onClick={() => {
                  setOnlyIncomplete(false);
                  setFilterText("");
                  setFilterGenre("");
                  const miss = movies.filter((m) => isEmpty(m.CATEGORY));
                  if (miss.length === 0) return alert("Всички имат категория ✅");
                  setMovies((prev) => {
                    const s = new Set(miss.map((x) => x.MOVIE_ID));
                    return [...prev].sort((a, b) => Number(s.has(b.MOVIE_ID)) - Number(s.has(a.MOVIE_ID)));
                  });
                  setOnlyIncomplete(true);
                }}
              />
            </div>

            {/* Решетка */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-6">
              {filteredMovies.map((movie) => {
                const missing = getMissingFields(movie);
                return (
                  <div
                    key={movie.MOVIE_ID}
                    className="overflow-hidden rounded-2xl bg-white/5 border border-white/10 shadow-md"
                  >
                    <div className="relative">
                      <img
                        src={movie.POSTER_URL || "https://via.placeholder.com/300x450?text=No+Poster"}
                        alt={movie.MOVIE_NAME}
                        className="w-full h-64 object-cover"
                      />
                      {missing.length > 0 && (
                        <span className="absolute top-2 left-2 text-[10px] px-2 py-1 rounded-full bg-amber-500 text-white">
                          ⚠️ {missing.length} празни
                        </span>
                      )}
                    </div>
                    <div className="p-3 text-white">
                      <div className="font-semibold leading-snug line-clamp-2 min-h-[2.5rem]">
                        {movie.MOVIE_NAME}
                      </div>
                      <div className="text-xs text-white/70 line-clamp-1">{movie.GENRE || "—"}</div>
                      <div className="text-xs text-white/60">{formatDate(movie.PREMIERE_DATE)}</div>
                      <div className="flex justify-between items-center gap-2 mt-2">
                        <button
                          onClick={() => {
                            setEditingMovie(movie);
                            setEditTab("main");
                            setTmdbPicked(null);
                            setTmdbModalResults([]);
                            setTmdbQuery(movie.MOVIE_NAME || "");
                          }}
                          className="text-xs px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          ✏️ Редакция
                        </button>
                        <button
                          onClick={() => handleDeleteMovie(movie.MOVIE_ID)}
                          className="text-xs px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white"
                        >
                          🗑 Изтриване
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {filteredMovies.length === 0 && (
                <div className="col-span-full text-center text-white/70 py-10">
                  Няма резултати. Променете филтрите.
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Модал за редакция */}
      {editingMovie && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3">
          <div className="absolute inset-0 bg-black/60" onClick={() => setEditingMovie(null)} />
          <div className="relative w-full max-w-4xl bg-white text-gray-900 rounded-2xl shadow-2xl overflow-hidden">

            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-bold">Редакция: {editingMovie.MOVIE_NAME}</h2>
              <button
                className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200"
                onClick={() => setEditingMovie(null)}
              >
                Затвори
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 p-3 border-b bg-gray-50 flex-wrap">
              <EditTab active={editTab === "main"} onClick={() => setEditTab("main")}>
                Основни
              </EditTab>
              <EditTab active={editTab === "media"} onClick={() => setEditTab("media")}>
                Медия
              </EditTab>
              <EditTab active={editTab === "classify"} onClick={() => setEditTab("classify")}>
                Класификация
              </EditTab>
              <EditTab active={editTab === "people"} onClick={() => setEditTab("people")}>
                Екип
              </EditTab>
              <EditTab active={editTab === "tmdb"} onClick={() => setEditTab("tmdb")}>
                TMDb помощник
              </EditTab>
            </div>

            {/* Body */}
            <div className="p-4 max-h-[70vh] overflow-auto">
              {editTab === "main" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <TextField
                    label="Заглавие"
                    field="MOVIE_NAME"
                    value={editingMovie.MOVIE_NAME}
                    onChange={setEditingMovie}
                    invalid={isEmpty(editingMovie.MOVIE_NAME)}
                  />
                  <TextField
                    label="Оригинално заглавие"
                    field="ORIGINAL_TITLE"
                    value={editingMovie.ORIGINAL_TITLE}
                    onChange={setEditingMovie}
                  />
                  <TextArea
                    label="Описание (PLOT) – предпочитаме BG"
                    field="PLOT"
                    value={editingMovie.PLOT || editingMovie.DESCRIPTION || ""}
                    onChange={setEditingMovie}
                    rows={5}
                    className="md:col-span-2"
                    invalid={isEmpty(editingMovie.PLOT)}
                  />
                  <TextField
                    label="Жанр"
                    field="GENRE"
                    value={editingMovie.GENRE}
                    onChange={setEditingMovie}
                    invalid={isEmpty(editingMovie.GENRE)}
                  />
                  <TextField
                    label="Премиерна дата"
                    type="date"
                    field="PREMIERE_DATE"
                    value={toYmd(editingMovie.PREMIERE_DATE)}
                    onChange={setEditingMovie}
                  />
                  <TextField
                    label="Продължителност (мин.)"
                    type="number"
                    field="DURATION"
                    value={editingMovie.DURATION}
                    onChange={setEditingMovie}
                    invalid={isEmpty(editingMovie.DURATION)}
                  />
                  <div className="grid gap-1">
                    <TextField
                      label="Оригинален език"
                      field="ORIGINAL_LANGUAGE"
                      value={editingMovie.ORIGINAL_LANGUAGE}
                      onChange={setEditingMovie}
                      disabled
                      help="Полето е заключено. Използвай бутона отдолу, за да се определи автоматично от TMDb/OMDb/Wiki."
                    />
                    <div>
                      <button
                        type="button"
                        onClick={smartFillLanguageInline}
                        className="mt-1 px-3 py-1.5 rounded-lg bg-slate-700 text-white text-xs"
                      >
                        🌐 Автопопълни език
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {editTab === "media" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <TextField
                    label="Плакат (URL)"
                    field="POSTER_URL"
                    value={editingMovie.POSTER_URL}
                    onChange={setEditingMovie}
                    invalid={isEmpty(editingMovie.POSTER_URL)}
                  />
                  <TextField
                    label="Снимка (URL)"
                    field="PHOTO_URL"
                    value={editingMovie.PHOTO_URL}
                    onChange={setEditingMovie}
                  />
                  <TextField
                    label="Трейлър (YouTube URL)"
                    field="TRAILER_URL"
                    value={editingMovie.TRAILER_URL}
                    onChange={setEditingMovie}
                    className="md:col-span-2"
                  />
                  {/* Преглед */}
                  <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="rounded-xl border p-3">
                      <div className="text-sm font-semibold mb-2">Преглед плакат</div>
                      <img
                        src={editingMovie.POSTER_URL || "https://via.placeholder.com/300x450?text=Poster"}
                        alt="poster"
                        className="w-full h-80 object-cover rounded-lg"
                      />
                    </div>
                    <div className="rounded-xl border p-3">
                      <div className="text-sm font-semibold mb-2">Преглед трейлър</div>
                      {editingMovie.TRAILER_URL ? (
                        <div className="aspect-video">
                          <iframe
                            src={editingMovie.TRAILER_URL.replace("watch?v=", "embed/")}
                            title="trailer"
                            className="w-full h-full rounded-lg"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                          />
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500">Няма трейлър</div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {editTab === "classify" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <SelectField
                    label="Категория (BG)"
                    field="CATEGORY"
                    value={editingMovie.CATEGORY || ""}
                    onChange={setEditingMovie}
                    options={BG_CATEGORIES}
                    invalid={isEmpty(editingMovie.CATEGORY)}
                  />
                  <TextField
                    label="Формат (напр. 2D, 3D, IMAX)"
                    field="BROADCAST_FORMAT"
                    value={editingMovie.BROADCAST_FORMAT || ""}
                    onChange={setEditingMovie}
                  />
                </div>
              )}

              {editTab === "people" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <TextField
                    label="Режисьор"
                    field="DIRECTOR"
                    value={editingMovie.DIRECTOR}
                    onChange={setEditingMovie}
                  />
                  <TextField
                    label="Актьори"
                    field="ACTORS"
                    value={editingMovie.ACTORS}
                    onChange={setEditingMovie}
                  />
                  <TextField
                    label="Продукция"
                    field="PRODUCTION"
                    value={editingMovie.PRODUCTION}
                    onChange={setEditingMovie}
                    className="md:col-span-2"
                  />
                </div>
              )}

              {editTab === "tmdb" && (
                <div className="grid gap-4">
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                    <input
                      value={tmdbQuery}
                      onChange={(e) => setTmdbQuery(e.target.value)}
                      onKeyDown={(e) => (e.key === "Enter" ? tmdbSearchInModal() : null)}
                      placeholder="Търси в TMDb (BG) …"
                      className="md:col-span-3 px-3 py-2 rounded-lg border"
                    />
                    <select
                      value={tmdbLang}
                      onChange={(e) => setTmdbLang(e.target.value)}
                      className="px-3 py-2 rounded-lg border"
                    >
                      <option value="bg-BG">bg-BG</option>
                      <option value="en-US">en-US</option>
                      <option value="de-DE">de-DE</option>
                    </select>
                    <button onClick={tmdbSearchInModal} className="px-3 py-2 rounded-lg bg-purple-600 text-white">
                      Търси
                    </button>
                  </div>

                  {tmdbModalResults.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      {tmdbModalResults.map((r) => (
                        <button
                          key={r.id}
                          onClick={async () => {
                            setTmdbPicked(r);
                            const det = await fetchDetails(r.id, tmdbLang);
                            applyDetailsToEditing(det, "fill-empty");
                          }}
                          className={`text-left rounded-xl overflow-hidden border hover:shadow ${
                            tmdbPicked?.id === r.id ? "border-purple-500" : "border-gray-200"
                          }`}
                          title="Попълни празните от този запис"
                        >
                          <img
                            src={
                              r.poster_path
                                ? `https://image.tmdb.org/t/p/w300${r.poster_path}`
                                : "https://via.placeholder.com/300x450?text=No+Image"
                            }
                            alt={r.title}
                            className="w-full h-44 object-cover"
                          />
                          <div className="p-2 text-sm">
                            <div className="font-semibold line-clamp-2">{r.title}</div>
                            <div className="text-xs text-gray-500">
                              {String(r.release_date || "").slice(0, 4) || "—"}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={async () => {
                        if (!tmdbPicked?.id) return alert("Моля изберете заглавие от резултатите.");
                        const det = await fetchDetails(tmdbPicked.id, tmdbLang);
                        applyDetailsToEditing(det, "fill-empty");
                      }}
                      className="px-3 py-2 rounded-lg bg-blue-600 text-white"
                    >
                      ⛏️ Попълни празните (от {tmdbLang})
                    </button>
                    <button
                      onClick={async () => {
                        if (!tmdbPicked?.id) return alert("Моля изберете заглавие от резултатите.");
                        const det = await fetchDetails(tmdbPicked.id, tmdbLang);
                        applyDetailsToEditing(det, "overwrite-all");
                      }}
                      className="px-3 py-2 rounded-lg bg-indigo-600 text-white"
                    >
                      🔄 Презапиши всички (от {tmdbLang})
                    </button>
                    <button onClick={setCategoryFromTMDb} className="px-3 py-2 rounded-lg bg-emerald-600 text-white">
                      ✅ Определи възраст (→ BG категория)
                    </button>
                    <button onClick={smartFillLanguageInline} className="px-3 py-2 rounded-lg bg-slate-700 text-white">
                      🌐 Само оригинален език (TMDb/OMDb/Wiki)
                    </button>
                    <button
                      onClick={async () => {
                        const title = editingMovie?.MOVIE_NAME || tmdbQuery;
                        const year = String(editingMovie?.PREMIERE_DATE || "").slice(0, 4);
                        const id = tmdbPicked?.id || (await tryResolveTmdbId(title, tmdbLang));
                        const bag = await fetchFromSources(id, title, year, tmdbLang);
                        applyDetailsToEditing(
                          {
                            original_title: bag.original_title,
                            overview: bag.overview,
                            release_date: bag.release_date,
                            runtime: bag.runtime,
                            genres: bag.genres,
                            production: bag.production,
                            language: bag.language,
                            poster: bag.poster,
                            trailer: bag.trailer,
                            director: bag.director,
                            actors: bag.actors,
                          },
                          "fill-empty"
                        );
                        if (bag.language)
                          setEditingMovie((p) => ({ ...p, ORIGINAL_LANGUAGE: bag.language }));
                      }}
                      className="px-3 py-2 rounded-lg bg-teal-600 text-white"
                    >
                      🤖 Попълни всички възможни полета (TMDb → OMDb → Wiki)
                    </button>
                    <button
                      onClick={() => {
                        const miss = getMissingFields(editingMovie);
                        if (miss.length === 0) return alert("Няма празни ключови полета ✅");
                        alert(`Липсват: ${miss.join(", ")}`);
                      }}
                      className="px-3 py-2 rounded-lg bg-amber-500 text-white"
                    >
                      🔎 Провери празни полета
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between gap-2 p-4 border-t bg-gray-50">
              <div className="text-sm text-gray-600">Празни: {getMissingFields(editingMovie).length}</div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleDeleteMovie(editingMovie.MOVIE_ID)}
                  className="px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white"
                >
                  🗑 Изтрий
                </button>
                <button onClick={saveEdit} className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white">
                  💾 Запази
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- UI компоненти ---------- */

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border transition ${
        active
          ? "bg-gradient-to-br from-[#6c56b5] to-[#2c204f] text-white border-white/20"
          : "bg-white/8 text-white border-white/15 hover:bg-white/12"
      }`}
    >
      {children}
    </button>
  );
}

function Badge({ children }) {
  return (
    <span className="ml-2 inline-flex items-center justify-center min-w-6 h-6 px-2 rounded-full text-xs bg-white/15 border border-white/20">
      {children}
    </span>
  );
}

function EditTab({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-lg text-sm border ${
        active
          ? "bg-white text-gray-900 border-gray-300"
          : "bg-white/70 text-gray-800 border-gray-200 hover:bg-white"
      }`}

    >
      {children}
    </button>
  );
}

function QuickAuditButton({ label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="px-3 py-1.5 rounded-lg text-sm bg-white/10 text-white border border-white/20 hover:bg-white/15"
    >
      {label}
    </button>
  );
}

function TextField({
  label,
  field,
  value,
  onChange,
  type = "text",
  className = "",
  invalid = false,
  disabled = false,
  help,
}) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-sm font-semibold mb-1">{label}</span>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange((p) => ({ ...p, [field]: e.target.value }))}
        className={`w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-purple-400 ${
          invalid ? "border-amber-500" : ""
        } ${disabled ? "bg-gray-100 text-gray-500 cursor-not-allowed" : ""}`}
        disabled={disabled}
      />
      {help && <div className="mt-1 text-xs text-gray-500">{help}</div>}
    </label>
  );
}

function TextArea({ label, field, value, onChange, rows = 4, className = "", invalid = false }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-sm font-semibold mb-1">{label}</span>
      <textarea
        rows={rows}
        value={value ?? ""}
        onChange={(e) => onChange((p) => ({ ...p, [field]: e.target.value }))}
        className={`w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-purple-400 ${
          invalid ? "border-amber-500" : ""
        }`}
      />
    </label>
  );
}

function SelectField({ label, field, value, onChange, options = [], className = "", invalid = false }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-sm font-semibold mb-1">{label}</span>
      <select
        value={value ?? ""}
        onChange={(e) => onChange((p) => ({ ...p, [field]: e.target.value }))}
        className={`w-full px-3 py-2 rounded-lg border bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 ${
          invalid ? "border-amber-500" : ""
        }`}
      >
        <option value="">—</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}
